import { startRegistration, startAuthentication } from '@simplewebauthn/browser';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Get authentication headers with JWT token
 */
const getHeaders = () => {
  const token = localStorage.getItem('sbwom_token') || sessionStorage.getItem('sbwom_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

/**
 * Register a new passkey for existing authenticated user
 * @param {string} deviceName - Optional device name
 * @returns {Object} Registration result
 */
export async function registerPasskey(deviceName = null) {
  try {
    // Step 1: Get registration options from server
    const optionsResponse = await fetch(`${API_BASE_URL}/passkey/register/start`, {
      method: 'POST',
      headers: getHeaders(),
    });

    if (!optionsResponse.ok) {
      const error = await optionsResponse.json();
      throw new Error(error.error || 'Failed to start registration');
    }

    const options = await optionsResponse.json();

    // Step 2: Create credential using device biometrics
    const credential = await startRegistration(options);

    // Step 3: Send credential to server for verification
    const verifyResponse = await fetch(`${API_BASE_URL}/passkey/register/finish`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        response: credential,
        deviceName,
      }),
    });

    if (!verifyResponse.ok) {
      const error = await verifyResponse.json();
      throw new Error(error.error || 'Failed to complete registration');
    }

    return await verifyResponse.json();
  } catch (error) {
    console.error('[PASSKEY] Registration error:', error);
    throw error;
  }
}

/**
 * Register a new user with passkey (no existing account)
 * @param {string} email - User email
 * @param {string} displayName - User display name
 * @param {string} deviceName - Optional device name
 * @param {string} inviteCode - Optional invite code
 * @returns {Object} Registration result with token
 */
export async function registerNewUserWithPasskey(email, displayName, deviceName = null, inviteCode = null) {
  try {
    // Step 1: Start registration
    const optionsResponse = await fetch(`${API_BASE_URL}/passkey/register/new-user/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, displayName }),
    });

    if (!optionsResponse.ok) {
      const error = await optionsResponse.json();
      throw new Error(error.error || 'Failed to start registration');
    }

    const { tempUserId, ...options } = await optionsResponse.json();

    // Step 2: Create credential
    const credential = await startRegistration(options);

    // Step 3: Complete registration
    const verifyResponse = await fetch(`${API_BASE_URL}/passkey/register/new-user/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tempUserId,
        response: credential,
        deviceName,
        inviteCode,
      }),
    });

    if (!verifyResponse.ok) {
      const error = await verifyResponse.json();
      throw new Error(error.error || 'Failed to complete registration');
    }

    return await verifyResponse.json();
  } catch (error) {
    console.error('[PASSKEY] New user registration error:', error);
    throw error;
  }
}

/**
 * Login with passkey
 * @param {string} email - Optional email to filter credentials
 * @returns {Object} Login result with token
 */
export async function loginWithPasskey(email = null) {
  try {
    // Step 1: Get authentication options
    const optionsResponse = await fetch(`${API_BASE_URL}/passkey/login/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!optionsResponse.ok) {
      const error = await optionsResponse.json();
      throw new Error(error.error || 'Failed to start authentication');
    }

    const { challengeId, ...options } = await optionsResponse.json();

    // Step 2: Authenticate with device biometrics
    const credential = await startAuthentication(options);

    // Step 3: Verify authentication
    const verifyResponse = await fetch(`${API_BASE_URL}/passkey/login/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        challengeId,
        response: credential,
      }),
    });

    if (!verifyResponse.ok) {
      const error = await verifyResponse.json();
      throw new Error(error.error || 'Failed to complete authentication');
    }

    return await verifyResponse.json();
  } catch (error) {
    console.error('[PASSKEY] Login error:', error);
    throw error;
  }
}

/**
 * Get list of user's registered passkeys
 * @returns {Array} List of passkeys
 */
export async function listPasskeys() {
  try {
    const response = await fetch(`${API_BASE_URL}/passkey/list`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch passkeys');
    }

    const data = await response.json();
    return data.passkeys || [];
  } catch (error) {
    console.error('[PASSKEY] List error:', error);
    throw error;
  }
}

/**
 * Delete a passkey
 * @param {string} passkeyId - Passkey ID
 * @returns {Object} Deletion result
 */
export async function deletePasskey(passkeyId) {
  try {
    const response = await fetch(`${API_BASE_URL}/passkey/${passkeyId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete passkey');
    }

    return await response.json();
  } catch (error) {
    console.error('[PASSKEY] Delete error:', error);
    throw error;
  }
}

/**
 * Create QR code session for cross-device authentication
 * @param {string} mode - 'login', 'register', or 'add-passkey'
 * @param {string} email - Optional email
 * @returns {Object} QR session data
 */
export async function createQRSession(mode, email = null) {
  try {
    // For add-passkey mode, include auth headers
    const headers = mode === 'add-passkey'
      ? getHeaders()
      : { 'Content-Type': 'application/json' };

    const response = await fetch(`${API_BASE_URL}/passkey/qr/create`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ mode, email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create QR session');
    }

    return await response.json();
  } catch (error) {
    console.error('[PASSKEY-QR] Create session error:', error);
    throw error;
  }
}

/**
 * Poll QR code session status (desktop waits for phone)
 * @param {string} sessionId - QR session ID
 * @returns {Object} Session status
 */
export async function pollQRSession(sessionId) {
  try {
    const response = await fetch(`${API_BASE_URL}/passkey/qr/poll/${sessionId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to poll session');
    }

    return await response.json();
  } catch (error) {
    console.error('[PASSKEY-QR] Poll error:', error);
    throw error;
  }
}

/**
 * Verify QR code authentication (phone completes auth)
 * @param {string} sessionId - QR session ID
 * @param {string} challengeId - Challenge ID
 * @param {Object} credential - WebAuthn credential response
 * @returns {Object} Verification result
 */
export async function verifyQRAuth(sessionId, challengeId, credential) {
  try {
    const response = await fetch(`${API_BASE_URL}/passkey/qr/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        challengeId,
        response: credential,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to verify QR authentication');
    }

    return await response.json();
  } catch (error) {
    console.error('[PASSKEY-QR] Verify error:', error);
    throw error;
  }
}

/**
 * Check if browser supports WebAuthn/Passkeys
 * @returns {boolean}
 */
export function isPasskeySupported() {
  return window.PublicKeyCredential !== undefined &&
         typeof window.PublicKeyCredential === 'function';
}

/**
 * Check if platform authenticator (Face ID/Touch ID) is available
 * @returns {Promise<boolean>}
 */
export async function isPlatformAuthenticatorAvailable() {
  try {
    if (!isPasskeySupported()) {
      return false;
    }
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (error) {
    console.error('[PASSKEY] Platform authenticator check error:', error);
    return false;
  }
}

export default {
  registerPasskey,
  registerNewUserWithPasskey,
  loginWithPasskey,
  listPasskeys,
  deletePasskey,
  createQRSession,
  pollQRSession,
  verifyQRAuth,
  isPasskeySupported,
  isPlatformAuthenticatorAvailable,
};
